import * as React from 'react'
import { onAuthStateChanged, signOut as firebaseSignOut, type User } from 'firebase/auth'

import { buildToolCards } from '@/features/commandDeck/config/toolMatrix'
import type { UserAccessSnapshot } from '@/features/commandDeck/contracts/types'
import { auth, FIREBASE_ENABLED } from '@/lib/firebase'

import { AuthSessionContext } from './AuthSessionContext'
import { VISITOR_ACCESS_SNAPSHOT } from './constants'
import { failClosedVisitorAccess, loadUserAccessSnapshot } from './loadUserAccessSnapshot'
import type { AuthSessionLegacyProfile, AuthSessionStatus, AuthSessionValue } from './types'

function toolsForAccess(access: UserAccessSnapshot) {
  return buildToolCards(access.accessState)
}

function anonymousSession(): Pick<
  AuthSessionValue,
  'status' | 'uid' | 'access' | 'tools' | 'legacy' | 'error'
> {
  return {
    status: 'anonymous',
    uid: undefined,
    access: VISITOR_ACCESS_SNAPSHOT,
    tools: toolsForAccess(VISITOR_ACCESS_SNAPSHOT),
    legacy: undefined,
    error: null,
  }
}

export function AuthSessionProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = React.useState<AuthSessionStatus>('loading')
  const [uid, setUid] = React.useState<string | undefined>()
  const [access, setAccess] = React.useState<UserAccessSnapshot>(VISITOR_ACCESS_SNAPSHOT)
  const [tools, setTools] = React.useState(() => toolsForAccess(VISITOR_ACCESS_SNAPSHOT))
  const [legacy, setLegacy] = React.useState<AuthSessionLegacyProfile | undefined>()
  const [error, setError] = React.useState<string | null>(null)

  const uidRef = React.useRef<string | undefined>()
  const userRef = React.useRef<User | null>(null)

  const applyLoaded = React.useCallback(
    (loaded: Awaited<ReturnType<typeof loadUserAccessSnapshot>>, nextUid: string) => {
      setUid(nextUid)
      setAccess(loaded.snapshot)
      setTools(toolsForAccess(loaded.snapshot))
      setLegacy(loaded.legacy)
      setError(null)
      setStatus('authenticated')
    },
    []
  )

  const applyFailClosed = React.useCallback((nextUid: string, message: string, displayName?: string) => {
    const closed = failClosedVisitorAccess(displayName)
    setUid(nextUid)
    setAccess(closed.snapshot)
    setTools(toolsForAccess(closed.snapshot))
    setLegacy(closed.legacy)
    setError(message)
    setStatus('authenticated')
  }, [])

  const resolveSnapshot = React.useCallback(
    async (user: User) => {
      setStatus('loading')
      uidRef.current = user.uid
      userRef.current = user
      try {
        const loaded = await loadUserAccessSnapshot(user.uid, user.displayName ?? undefined)
        if (uidRef.current !== user.uid) return
        applyLoaded(loaded, user.uid)
      } catch (e) {
        if (uidRef.current !== user.uid) return
        const message = e instanceof Error ? e.message : 'Unable to load access profile'
        applyFailClosed(user.uid, message, user.displayName ?? undefined)
      }
    },
    [applyFailClosed, applyLoaded]
  )

  const refreshAccess = React.useCallback(async () => {
    const user = userRef.current
    if (!user?.uid) return
    await resolveSnapshot(user)
  }, [resolveSnapshot])

  const signOut = React.useCallback(async () => {
    if (FIREBASE_ENABLED && auth) {
      await firebaseSignOut(auth)
    }
    uidRef.current = undefined
    userRef.current = null
    const next = anonymousSession()
    setStatus(next.status)
    setUid(next.uid)
    setAccess(next.access)
    setTools(next.tools)
    setLegacy(next.legacy)
    setError(next.error)
  }, [])

  React.useEffect(() => {
    if (!FIREBASE_ENABLED || !auth) {
      const next = anonymousSession()
      setStatus(next.status)
      setUid(next.uid)
      setAccess(next.access)
      setTools(next.tools)
      setLegacy(next.legacy)
      setError(next.error)
      return
    }

    const unsub = onAuthStateChanged(
      auth,
      (user) => {
        if (!user) {
          uidRef.current = undefined
          userRef.current = null
          const next = anonymousSession()
          setStatus(next.status)
          setUid(next.uid)
          setAccess(next.access)
          setTools(next.tools)
          setLegacy(next.legacy)
          setError(next.error)
          return
        }
        userRef.current = user
        void resolveSnapshot(user)
      },
      () => {
        uidRef.current = undefined
        userRef.current = null
        const next = anonymousSession()
        setStatus(next.status)
        setUid(next.uid)
        setAccess(next.access)
        setTools(next.tools)
        setLegacy(next.legacy)
        setError('Authentication session error')
      }
    )

    return () => unsub()
  }, [resolveSnapshot])

  const value = React.useMemo<AuthSessionValue>(
    () => ({
      status,
      uid,
      access,
      tools,
      legacy,
      error,
      refreshAccess,
      signOut,
    }),
    [status, uid, access, tools, legacy, error, refreshAccess, signOut]
  )

  return <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>
}
